import React, { useMemo } from 'react';
import type {
    MUIDataTableOptions,
    MUIDataTableColumn,
    MUIDataTableProps,
} from 'mui-datatables';
import MUIDataTable from 'mui-datatables';
import { applyFilterList } from '@forvalter/utils';
import { usePersistentFilters } from './usePersistentFilter';
import type { FilterTableKey } from './config';

type PersistentMUIDataTableProps = MUIDataTableProps & {
    tableKey: FilterTableKey;
};

export const PersistentFilterTable: React.FC<PersistentMUIDataTableProps> = ({
    columns,
    options,
    tableKey,
    ...muiTableProps
}) => {
    const { savedFilterList, handleFilterChange } =
        usePersistentFilters(tableKey);
    const baseColumns = columns as Array<MUIDataTableColumn>;

    const hydratedColumns = useMemo(
        () => applyFilterList(baseColumns, savedFilterList),
        [baseColumns, savedFilterList],
    );

    const handleChange: MUIDataTableOptions['onFilterChange'] = (
        changedColumn,
        filterList,
        type,
        changedColumnIndex,
        data,
    ) => {
        handleFilterChange(changedColumn, filterList);

        // Call the external handler with the updated state
        options?.onFilterChange?.(
            changedColumn,
            filterList,
            type,
            changedColumnIndex,
            data,
        );
    };

    const handleTableChange: MUIDataTableOptions['onTableChange'] = (
        action,
        tableState,
    ) => {
        if (action === 'resetFilters' || action === 'filterChange') {
            const filterList = tableState.filterList ?? [];

            handleFilterChange(null, filterList);
        }

        // For all actions, call the handler directly
        options?.onTableChange?.(action, tableState);
    };

    const mergedOptions: MUIDataTableOptions = {
        ...options,
        onFilterChange: handleChange,
        onTableChange: handleTableChange,
    };

    return (
        <MUIDataTable
            columns={hydratedColumns}
            options={mergedOptions}
            {...muiTableProps}
        />
    );
};
